import React from 'react';
import classNames from 'classnames';

interface PullToRefreshIndicatorProps {
  pullDistance: number;
  pullProgress: number;
  isRefreshing: boolean;
  threshold?: number;
  className?: string;
}

/**
 * Visual indicator for pull-to-refresh functionality
 * Shows loading spinner and pull progress
 */
const PullToRefreshIndicator: React.FC<PullToRefreshIndicatorProps> = ({
  pullDistance,
  pullProgress,
  isRefreshing,
  threshold = 80,
  className = ''
}) => {
  const showIndicator = pullDistance > 10 || isRefreshing;
  const isReady = pullProgress >= 1;

  if (!showIndicator) return null;

  return (
    <div
      className={classNames(
        'flex justify-center items-center transition-all duration-300',
        className
      )}
      style={{
        height: `${threshold}px`,
        marginTop: isRefreshing ? 0 : `-${threshold}px`,
        transform: `translateY(${isRefreshing ? 0 : Math.min(pullDistance, threshold)}px)`,
        opacity: pullDistance > 10 || isRefreshing ? 1 : 0
      }}
    >
      <div className="flex items-center justify-center">
        {isRefreshing ? (
          <>
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Refreshing...</span>
          </>
        ) : (
          <div
            className={classNames(
              'transition-transform duration-200',
              isReady ? 'scale-110' : 'scale-100'
            )}
            style={{
              transform: `rotate(${pullProgress * 180}deg)`
            }}
          >
            <svg
              className={classNames(
                'w-6 h-6 transition-colors',
                isReady ? 'text-primary' : 'text-gray-400 dark:text-gray-400'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default PullToRefreshIndicator;